import { useCallback, useEffect, useRef } from 'react'
import type { MutableRefObject } from 'react'
import type { ApolloError } from '@apollo/client'
import { useMutation, useQuery } from '@truepill/tpos-react-router'
import { CLEAR_ORDER_LOCK, GET_ORDER_LOCK, SET_ORDER_LOCK } from 'gql'
import type { GraphQLError } from 'graphql'
import { usePlusClient } from 'providers/VisionRouter'
import { isNil } from 'ramda'
import type { Lock, Order, User, UserFirstLastName } from 'types'
import { normalizeOrderId } from 'utils'
import { updateOrder } from 'utils/subscribeToolkit'

type LockWithPartialUser = Omit<Lock, 'user'> & { user: Pick<User, '_id' | 'firstName' | 'lastName'> }

/**
 * @param orderId If undefined, then the callbacks do nothing. This is for use
 *                in components with possibly undefined order IDs.
 */
export default function useOrderLock(
  orderId?: string,
  shouldSkip = false,
): {
  orderEditable: boolean
  orderLock?: LockWithPartialUser
  orderLockedBy?: UserFirstLastName
  isOrderLockedByMe: boolean
  setOrderLock: () => Promise<void>
  clearOrderLock: () => Promise<LockWithPartialUser | undefined>
  acquiredByMeRef: MutableRefObject<boolean>
} {
  const forbiddenRef = useRef(false)
  // True once this hook instance has successfully acquired the lock. Local source of truth for
  // "this session holds the lock" — reliable regardless of GET_ORDER_LOCK freshness.
  const acquiredByMeRef = useRef(false)
  // Reset refs when orderId changes
  useEffect(() => {
    forbiddenRef.current = false
    acquiredByMeRef.current = false
  }, [orderId])
  const { tokenContext } = usePlusClient()
  const normalizedOrderId = normalizeOrderId(orderId)

  const { loading, error, data, subscribeToMore } = useQuery<{ order: { lock?: LockWithPartialUser } }>(
    GET_ORDER_LOCK,
    {
      skip: isNil(normalizedOrderId) || shouldSkip,
      variables: { orderId: normalizedOrderId },
      fetchPolicy: 'no-cache',
    },
  )

  const [setOrderLockMutation, { error: orderLockError }] = useMutation<{
    order?: Pick<Order, '_id'> & {
      lock: LockWithPartialUser
    }
  }>(SET_ORDER_LOCK)

  const [clearOrderLockMutation, { data: orderClearData, error: orderClearError }] = useMutation<{
    order?: Pick<Order, '_id'> & {
      lock: LockWithPartialUser
    }
  }>(CLEAR_ORDER_LOCK)

  /**
   * @returns The updated lock information. This may not be the expected lock
   *          (check the lock's user ID to be sure). If nil, there is no lock
   *          despite the attempt.
   * @throws If there was an error in the mutation (such as an invalid order).
   */
  const setOrderLock = useCallback(async () => {
    // Avoid trying to get lock multiple times when getting GraphQL Forbidden errors
    if (!normalizedOrderId || forbiddenRef.current) {
      return
    }
    try {
      const result = await setOrderLockMutation({ variables: { orderId: normalizedOrderId } })
      // lockOrder doesn't throw when another user holds the lock; it returns their lock (or null).
      // We only own it when the returned lock is ours.
      acquiredByMeRef.current = result.data?.order?.lock?.user._id === tokenContext?.id
    } catch (error) {
      const isForbiddenError = (error as ApolloError)?.graphQLErrors?.find(
        (err: GraphQLError & { extensions?: { code?: string } }) => err.extensions?.code === 'FORBIDDEN',
      )
      if (isForbiddenError) {
        forbiddenRef.current = true
      }
      throw error
    }

    if (orderLockError) {
      console.error(orderLockError)
      throw new Error(orderLockError.toString())
    }
  }, [normalizedOrderId, setOrderLockMutation, orderLockError, tokenContext?.id])

  /**
   * @returns The updated lock information. If nil, the order has successfully
   *          been unlocked; if still defined, then the attempt to unlock was
   *          unsuccessful.
   * @throws If there was an error in the mutation (such as an invalid order).
   */
  const clearOrderLock = async () => {
    if (!normalizedOrderId) {
      return
    }

    await clearOrderLockMutation({ variables: { orderId: normalizedOrderId } })
    acquiredByMeRef.current = false

    if (orderClearError) {
      console.error(orderClearError)
      throw new Error(orderClearError.toString())
    }

    return orderClearData?.order?.lock
  }

  useEffect(() => {
    if (normalizedOrderId) {
      return updateOrder(subscribeToMore, { orderId: normalizedOrderId })
    }
  }, [subscribeToMore, normalizedOrderId])

  const isOrderLockedByMe = data?.order?.lock?.user._id === tokenContext?.id
  let orderEditable = false

  if (!normalizedOrderId || (!!tokenContext && (!data?.order?.lock || isOrderLockedByMe))) {
    orderEditable = true
  }

  return {
    orderEditable,
    isOrderLockedByMe,
    orderLockedBy: data?.order?.lock?.user,
    orderLock:
      loading || error || !data || (data.order?.lock?.expiresAt ?? Infinity) < Date.now()
        ? undefined
        : data.order?.lock,
    setOrderLock,
    clearOrderLock,
    acquiredByMeRef,
  }
}
